import { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { since } from "@/lib/lastSeen";
import FilterableTable from "@/components/FilterableTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StaffPayrollPreview from "@/components/StaffPayrollPreview";
import PayNoticeDialog from "@/components/PayNoticeDialog";
import { describeNotice } from "@/lib/approvals";
import StaffPayHistory from "@/components/StaffPayHistory";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function StaffingPage() {
  const [people, setPeople] = useState([]);
  const [view, setView] = useState("active");
  const [genOpen, setGenOpen] = useState(false);
  const [genRows, setGenRows] = useState([]);
  const [genDays, setGenDays] = useState({});
  const [genAmounts, setGenAmounts] = useState({});
  const [month, setMonth] = useState(thisMonth());
  const [statements, setStatements] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("people");
  const [notice, setNotice] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", position: "", client: "",
    doj: "", monthly_amount: "",
  });

  const load = async (status) => {
    try {
      const { data } = await api.get("/staffing", { params: { status: status ?? view } });
      setPeople(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadStatements = async (m) => {
    try {
      const { data } = await api.get("/staffing/statements", { params: { month: m } });
      setStatements(data || []);
    } catch (e) { setStatements([]); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadStatements(month); }, [month]);

  const addPerson = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/staffing", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", email: "", phone: "", position: "", client: "", doj: "", monthly_amount: "" });
      setAdding(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const TDS_CHOICES = [
    ["threshold", "10% over 50k"],
    ["always", "10% from day 1"],
    ["none", "Not deducted"],
  ];
  // Contractors created before modes existed only carry the old boolean.
  const tdsModeOf = (p) =>
    TDS_CHOICES.some(([v]) => v === p.tds_mode)
      ? p.tds_mode
      : p.tds_enabled === false
      ? "none"
      : "threshold";
  const tdsLabel = (p) =>
    (TDS_CHOICES.find(([v]) => v === tdsModeOf(p)) || ["", ""])[1];

  const setTdsMode = async (p, mode) => {
    if (mode === tdsModeOf(p)) return;
    const label = (TDS_CHOICES.find(([v]) => v === mode) || ["", mode])[1];
    const msg =
      `Set TDS for ${p.name} to "${label}"?\n\n` +
      "This applies to statements you generate from now on. " +
      "Months already generated are not changed.";
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/staffing/${p.id}`, { tds_mode: mode });
      toast.success(`TDS for ${p.name}: ${label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inviteOne = async (p) => {
    if (!window.confirm(`Email a set-password link to ${p.name} at ${p.email}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staffing/${p.id}/send-invite`);
      if (data?.ok) toast.success(`Invite sent to ${p.email}`);
      else toast.error(data?.error || "Could not send");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const inviteAll = async () => {
    if (!window.confirm("Email a set-password link to everyone who has not set one up yet?")) return;
    setBusy(true);
    try {
      const d = (await api.post("/staffing/send-invites", {})).data || {};
      toast.success(`Sent ${d.sent || 0}`);
      (d.failed || []).forEach((f) => toast.error(`${f.name}: ${f.error}`));
      (d.skipped || []).forEach((f) => toast.info(`Skipped ${f.name}: ${f.reason}`));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const notifyMonth = async () => {
    if (!window.confirm(`Email everyone that their ${month} statement is ready?`)) return;
    setBusy(true);
    try {
      const d = (await api.post("/staffing/statements/notify", { month })).data || {};
      toast.success(`Sent ${d.sent || 0}`);
      (d.failed || []).forEach((f) => toast.error(`${f.name}: ${f.error}`));
      (d.skipped || []).forEach((f) => toast.info(`Skipped ${f.name}: ${f.reason}`));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const bankFile = () => {
    const d = new Date();
    const def = `${String(d.getDate()).padStart(2, "0")}${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
    const pd = window.prompt("Payment date for the bank file (DD/MM/YYYY)", def);
    if (pd) window.open(`/api/staffing/enet-export?month=${month}&payment_date=${pd}`, "_blank");
  };

  const monthDayCount = (m) =>
    new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0).getDate();
  const startsOrEndsIn = (p, m) =>
    String(p.doj || "").slice(0, 7) === m ||
    String(p.last_working_day || "").slice(0, 7) === m;

  // Anyone who joined or left mid-month needs a figure typed in. Paying them
  // a whole month would be wrong, and guessing it on your behalf is worse.
  const openGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/staffing", { params: { status: "active" } });
      // The rate that applies in this month, not whatever the record says
      // today - otherwise a mid-month raise stays invisible until after the
      // payslip has been cut.
      let rates = {};
      try {
        const pv = await api.get("/staffing/payroll-preview", { params: { month } });
        (pv.data.rows || []).forEach((r) => { rates[r.staff_id] = r; });
      } catch (e) { /* the run still works without the preview */ }
      setGenRows((data || []).map((p) => ({
        ...p,
        monthly_amount: rates[p.id] ? rates[p.id].monthly_amount : p.monthly_amount,
        segments: rates[p.id] ? rates[p.id].segments : null,
        rate_changed_mid_month: rates[p.id] ? rates[p.id].rate_changed_mid_month : false,
        partial: startsOrEndsIn(p, month),
      })));
      setGenDays({});
      setGenAmounts({});
      setGenOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runGenerate = async () => {
    const days = {};
    const amounts = {};
    genRows.forEach((p) => {
      const d = genDays[p.id];
      if (d !== undefined && String(d).trim() !== "") days[p.id] = Number(d);
      const a = genAmounts[p.id];
      if (a !== undefined && String(a).trim() !== "") amounts[p.id] = Number(a);
    });
    setBusy(true);
    try {
      const { data } = await api.post("/staffing/generate", { month, days, amounts });
      toast.success(`Generated ${data.count} statement(s) for ${month}`);
      setGenOpen(false);
      loadStatements(month);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const monthTotal = statements.reduce((t, s) => t + Number(s.net || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Staffing</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Contractors placed with clients. Paid a fixed monthly amount — separate from consultants.
          </p>
        </div>
        <Button variant="outline" className="mr-2" disabled={busy}
                onClick={inviteAll} data-testid="staff-invite-all">Email logins</Button>
        <Button onClick={() => { setTab("people"); setAdding((v) => !v); }} data-testid="staff-add-toggle">
          {adding ? "Cancel" : "Add contractor"}
        </Button>
      </div>

    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="bg-zinc-100">
        <TabsTrigger value="people" data-testid="staff-tab-people">People</TabsTrigger>
        <TabsTrigger value="payroll" data-testid="staff-tab-payroll">Payroll</TabsTrigger>
      </TabsList>

      <TabsContent value="people" className="space-y-6 mt-4">
      {adding ? (
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">New contractor</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={addPerson} className="grid grid-cols-2 gap-3">
              {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"],
                ["position", "Position"], ["client", "Client"], ["doj", "Start date (YYYY-MM-DD)"],
                ["monthly_amount", "Monthly amount"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-zinc-500">{label}</label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
              <div className="col-span-2 flex items-center justify-between pt-2">
                <p className="text-xs text-zinc-500">
                  TDS is applied by default once they cross ₹50,000 in a financial year.
                </p>
                <Button type="submit" disabled={busy} data-testid="staff-add-submit">
                  {busy ? "Saving..." : "Add"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <FilterableTable
              kind="staffing"
              rows={people}
              rowKey={(p) => p.id}
              emptyText="No contractors yet."
              columns={[
                { key: "name", label: "Name",
                  render: (p) => (
                    <>
                      <button className="text-left hover:underline font-medium"
                              onClick={() => setDocsFor(p)}
                              data-testid={`staff-docs-${p.id}`}>{p.name}</button>
                      <div className="text-xs text-zinc-500">{p.email}</div>
                    </>
                  ) },
                { key: "client", label: "Client" },
                { key: "position", label: "Position" },
                { key: "monthly_amount", label: "Monthly", align: "right",
                  value: (p) => String(p.monthly_amount ?? ""),
                  render: (p) => inr(p.monthly_amount) },
                { key: "tds_enabled", label: "TDS",
              value: (p) => tdsLabel(p),
              render: (p) => (
                <select value={tdsModeOf(p)}
                        onChange={(e) => setTdsMode(p, e.target.value)}
                        data-testid={`staff-tds-${p.id}`}
                        className="border border-zinc-300 rounded px-1 py-0.5 text-xs bg-white">
                  {TDS_CHOICES.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              ) },
                { key: "status", label: "Status",
                  render: (p) => <Badge variant="outline">{p.status}</Badge> },
                { key: "last_seen_at", label: "Last seen", type: "date",
                  value: (p) => since(p.last_seen_at),
                  render: (p) => (
                    <span className={p.last_seen_at ? "" : "text-amber-700"}>
                      {since(p.last_seen_at)}
                    </span>
                  ) },
                { key: "login", label: "Login", align: "right",
                  value: () => "",
                  render: (p) => (p.email ? (
                    <button className="text-xs text-sky-700 hover:underline"
                            onClick={() => inviteOne(p)}
                            data-testid={`staff-invite-${p.id}`}>
                      {p.password_set_at ? "New password" : "Email login"}
                    </button>
                  ) : null) },
              ]}
            />
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="payroll" className="space-y-6 mt-4">
        <StaffPayrollPreview month={month} refreshKey={statements.length} />

      <Card className="border-zinc-200 shadow-none">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Payroll</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-md border border-zinc-200 p-0.5">
              {[["active", "Active"], ["exited", "Exited"]].map(([v, label]) => (
                <button key={v} type="button"
                        onClick={() => { setView(v); load(v); }}
                        data-testid={`staff-view-${v}`}
                        className={`px-3 py-1 text-xs rounded ${view === v ? "bg-emerald-600 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
                  {label}
                </button>
              ))}
            </div>
            <Input type="month" value={month} className="h-8 w-40"
                     onChange={(e) => setMonth(e.target.value)} data-testid="staff-month" />
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={bankFile} data-testid="staff-bankfile">Bank file</Button>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setNotice("ready")} data-testid="staff-notify-ready">
                Payslip ready
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setNotice("paid")} data-testid="staff-notify-paid">
                Record payment
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={openGenerate} data-testid="staff-generate">
                {busy ? "Working..." : "Run payroll"}
              </Button>

            <PayNoticeDialog
        open={!!notice} onOpenChange={(v) => setNotice(v ? notice : null)}
        mode={notice} busy={busy}
        rows={(statements || []).map((st) => ({
          id: st.id, name: st.name, amount: st.net ?? st.gross,
          notified_ready_at: st.notified_ready_at,
          notified_paid_at: st.notified_paid_at, paid_on: st.paid_on,
        }))}
        onConfirm={async (ids, when, alsoEmail) => {
          setBusy(true);
          try {
            const url = notice === "paid"
              ? "/staffing/statements/mark-paid"
              : "/staffing/statements/pay-notice";
            const body = notice === "paid"
              ? { statement_ids: ids, payment_date: when, notify: alsoEmail }
              : { statement_ids: ids, kind: "ready" };
            const { data } = await api.post(url, body);
            toast.success(describeNotice(data, notice));
            (data.skipped || []).forEach((k) =>
              toast.message(`${k.name} skipped`, { description: k.reason }));
            (data.failed || []).forEach((k) =>
              toast.error(`${k.name}: ${k.error}`));
            setNotice(null);
            loadStatements(month);
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setBusy(false); }
        }} />

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
              <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="gen-dialog">
                <DialogHeader>
                  <DialogTitle className="text-base font-semibold">
                    Run payroll — {month}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-xs text-zinc-600">
                  Re-running replaces this month rather than adding to it.
                </p>
                <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100">
                  <div className="px-3 py-2 text-xs text-zinc-600">
                    Days to pay, or type an amount to pay exactly that. Leave both blank
                    for a whole month. An amount overrides the days. Anyone who joined or
                    left this month is highlighted and needs one of the two.
                  </div>
                  {genRows.map((p) => (
                    <div key={p.id}
                         className={`flex items-center gap-2 px-3 py-2 text-sm ${p.partial ? "bg-amber-50" : ""}`}>
                      <span className="flex-1">
                        {p.name}
                        {p.rate_changed_mid_month && p.segments && p.segments[1] && (
                          <span className="text-[11px] text-amber-800">
                            {` rate changes ${p.segments[1].from.slice(8)}/${p.segments[1].from.slice(5, 7)}`}
                          </span>
                        )}
                        {p.partial && (
                          <span className="text-[11px] text-amber-800">
                            {String(p.doj || "").slice(0, 7) === month
                              ? ` started ${p.doj}`
                              : ` last day ${p.last_working_day}`}
                          </span>
                        )}
                      </span>
                      <input type="number" min="1" max={monthDayCount(month)}
                             value={genDays[p.id] ?? ""}
                             placeholder={p.partial ? "required" : "full"}
                             onChange={(e) => setGenDays({ ...genDays, [p.id]: e.target.value })}
                             data-testid={`gen-days-${p.id}`}
                             className={`w-24 border rounded px-2 py-1 text-sm ${p.partial ? "border-amber-400" : "border-zinc-300"}`} />
                      <span className="text-[11px] text-zinc-600 w-16">of {monthDayCount(month)}</span>
                      <input type="number" min="0" step="0.01"
                             value={genAmounts[p.id] ?? ""}
                             placeholder="amount"
                             onChange={(e) => setGenAmounts({ ...genAmounts, [p.id]: e.target.value })}
                             data-testid={`gen-amount-${p.id}`}
                             className="w-28 border border-zinc-300 rounded px-2 py-1 text-sm" />
                      <span className="text-[11px] text-zinc-500 w-28 text-right tabular-nums">
                        {(() => {
                          const a = genAmounts[p.id];
                          if (a !== undefined && String(a).trim() !== "")
                            return "\u20b9 " + Number(a).toLocaleString("en-IN");
                          const d = genDays[p.id];
                          const full = Number(p.monthly_amount || 0);
                          if (d !== undefined && String(d).trim() !== "")
                            return "\u20b9 " + Math.round(full * Number(d) / monthDayCount(month)).toLocaleString("en-IN");
                          return "\u20b9 " + full.toLocaleString("en-IN");
                        })()}
                      </span>
                    </div>
                  ))}
                  {genRows.length === 0 && (
                    <div className="px-3 py-4 text-xs text-zinc-500">No active contractors.</div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
                  <Button size="sm" data-testid="gen-confirm"
                          disabled={busy || genRows.some((p) => {
                            const raw = genDays[p.id];
                            const amt = genAmounts[p.id];
                            const hasAmt = amt !== undefined && String(amt).trim() !== "";
                            if (hasAmt) return Number(amt) < 0;
                            const blank = raw === undefined || String(raw).trim() === "";
                            if (blank) return p.partial;
                            const v = Number(raw);
                            return !v || v < 1 || v > monthDayCount(month);
                          })}
                          onClick={runGenerate}>
                    {busy ? "Working\u2026" : "Run payroll"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead><TableHead>Client</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">TDS</TableHead>
              <TableHead className="text-right">Payable</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {statements.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{s.client || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(s.gross)}</TableCell>
                  <TableCell className="text-right tabular-nums text-zinc-500">
                    {Number(s.tds || 0) ? inr(s.tds) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{inr(s.net)}</TableCell>
                </TableRow>
              ))}
              {statements.length ? (
                <TableRow><TableCell colSpan={4} className="text-right font-semibold">Total</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{inr(monthTotal)}</TableCell>
                </TableRow>
              ) : (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">
                  Nothing generated for {month} yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </TabsContent>
    </Tabs>
      <StaffingDocuments person={docsFor} onClose={() => setDocsFor(null)}
                        onSaved={async (u) => { await load(); setDocsFor(u); }} />
      </div>
  );
}

// ------------------------------------------------------- document folder
//
// Mirrors the employee document folder. Upload and delete are admin-only;
// the contractor sees the same list from their own portal and can download
// but not change it.

const STAFF_DOC_CATEGORIES = [
  ["agreement", "Agreement"],
  ["offer_letter", "Offer letter"],
  ["invoice", "Invoice"],
  ["id_proof", "ID proof"],
  ["bank_proof", "Bank proof"],
  ["statement", "Statement"],
  ["other", "Other"],
];

const STAFF_DOC_LABELS = Object.fromEntries(STAFF_DOC_CATEGORIES);

function docSize(n) {
  const kb = (Number(n) || 0) / 1024;
  return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
}


// Everything the contractor fills in for themselves, plus the terms the admin
// set. Same shape as the doctor detail panel deliberately - it is the same
// conversation, so it should not look like a different app.
const STAFF_DETAIL_SECTIONS = [
  ["Engagement", [
    ["client", "Client"],
    ["position", "Position"],
    ["monthly_amount", "Monthly amount"],
    ["doj", "Started"],
    ["tds_enabled", "TDS"],
    ["email", "Login email"],
  ]],
  ["Contact", [
    ["phone", "Phone"],
    ["dob", "Date of birth"],
    ["address", "Address"],
  ]],
  ["Emergency contact", [
    ["emergency_contact_name", "Name"],
    ["emergency_contact_phone", "Phone"],
  ]],
  ["Payment details", [
    ["account_holder_name", "Account holder"],
    ["bank_name", "Bank"],
    ["bank_account", "Account number"],
    ["bank_ifsc", "IFSC"],
    ["upi_id", "UPI ID"],
    ["pan", "PAN"],
  ]],
];

// Mirrors the allowed tuple in update_staffing (backend/server.py). A field
// shown as editable but missing there would look saveable and silently not be.
// Login email and TDS are deliberately absent: email is not accepted by the
// endpoint, and TDS is the three-way chooser on the roster.
const STAFF_API_EDITABLE = [
  "name", "phone", "position", "client", "doj", "monthly_amount",
  "account_holder_name", "bank_name", "bank_account", "bank_ifsc", "pan",
  "dob", "address", "upi_id", "emergency_contact_name", "emergency_contact_phone",
];
const STAFF_EDITABLE = STAFF_DETAIL_SECTIONS
  .flatMap(([, f]) => f.map(([k]) => k))
  .filter((k) => STAFF_API_EDITABLE.includes(k));

const STAFF_FIELD_LABELS = Object.fromEntries(
  STAFF_DETAIL_SECTIONS.flatMap(([, f]) => f)
);

// Mirrors STAFF_REQUIRED_PROFILE in backend/server.py. Kept in step by hand.
const STAFF_REQUIRED = ["phone", "pan", "account_holder_name", "bank_name",
                        "bank_account", "bank_ifsc", "address",
                        "emergency_contact_name", "emergency_contact_phone"];

const staffMissing = (p) =>
  STAFF_REQUIRED.filter((f) => !String((p || {})[f] || "").trim());

function StaffRow({ label, value }) {
  const filled = String(value == null ? "" : value).trim();
  return (
    <div className="flex justify-between gap-3 border-b border-zinc-100 py-1.5">
      <span className="text-[13px] text-zinc-500 shrink-0">{label}</span>
      <span className="text-[13px] text-zinc-900 text-right break-all">
        {filled || <span className="text-zinc-300">not filled in</span>}
      </span>
    </div>
  );
}

function StaffingDocuments({ person, onClose, onSaved }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("agreement");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [exitDay, setExitDay] = useState("");
  const [exitReason, setExitReason] = useState("");
  const [exitBusy, setExitBusy] = useState(false);
  const [adminStatements, setAdminStatements] = useState([]);

  // `pid` is declared further down, and a dependency array is evaluated
  // during render - referencing it here would throw before it exists.
  useEffect(() => {
    const sid = person ? person.id : null;
    if (!sid) { setAdminStatements([]); return; }
    api.get(`/staffing/${sid}/statements`)
      .then((r) => setAdminStatements(r.data || []))
      .catch(() => setAdminStatements([]));
  }, [person]);

  const grabPdf = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = u;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const money = (v) => "\u20b9 " + Number(v || 0).toLocaleString("en-IN");
  const pid = person ? person.id : null;

  useEffect(() => {
    if (!pid) { setDocs([]); return; }
    let live = true;
    api.get(`/staffing/${pid}/documents`)
      .then(({ data }) => { if (live) setDocs(data || []); })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { live = false; };
  }, [pid]);

  const reload = async () => {
    const { data } = await api.get(`/staffing/${pid}/documents`);
    setDocs(data || []);
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("note", note);
    setBusy(true);
    try {
      await api.post(`/staffing/${pid}/documents`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setNote("");
      // Clearing the input matters: picking the same file twice in a row
      // fires no change event otherwise.
      if (fileRef.current) fileRef.current.value = "";
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = () => {
    const f = {};
    STAFF_EDITABLE.forEach((k) => { f[k] = (person || {})[k] || ""; });
    setForm(f);
    setEditing(true);
  };

  const doExit = async () => {
    setExitBusy(true);
    try {
      const { data } = await api.post(`/staffing/${pid}/exit`,
        { last_working_day: exitDay, reason: exitReason });
      toast.success("Exit recorded");
      setExiting(false);
      if (onSaved) onSaved(data.contractor);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExitBusy(false);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/staffing/${pid}`, form);
      toast.success("Details updated");
      setEditing(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/staffing/${pid}/documents/${d.id}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!person) return null;
  return (
    <Dialog open={!!person} onOpenChange={(o) => { if (!o) { setEditing(false); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto space-y-4" data-testid="staff-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {person.name}
            <Badge variant="outline" className={person.status === "active"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-zinc-100 text-zinc-600"}>{person.status}</Badge>
            {editing ? null : (
              <div className="ml-auto flex items-center gap-3">
                <button className="text-xs font-normal text-emerald-700 hover:underline"
                        onClick={startEdit} data-testid="staff-edit">
                  Edit details
                </button>
                {person.status === "active" && (
                  <button className="text-xs font-normal text-red-700 hover:underline"
                          onClick={() => setExiting(true)} data-testid="staff-exit">
                    Exit contractor
                  </button>
                )}
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

          {pid && (
            <StaffPayHistory staffId={pid}
                             onChanged={(c) => { if (onSaved && c) onSaved(c); }} />
          )}

          {/* The same statements the contractor sees on their own portal. */}
          <div className="rounded-lg border border-zinc-200">
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-zinc-100">
              <div className="text-sm font-semibold">Monthly statements</div>
              {adminStatements.length > 0 && (
                <button type="button" data-testid="admin-payslips-all"
                        onClick={() => grabPdf(`/staffing/${pid}/payslips.pdf`, `payslips-${person.name}.pdf`)}
                        className="text-xs text-emerald-700 hover:underline">
                  Download all payslips
                </button>
              )}
            </div>
            {adminStatements.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-500">
                Nothing generated yet. Use Run payroll on the Staffing page once the month is paid.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                    <th className="text-left font-medium px-3 py-1.5">Month</th>
                    <th className="text-right font-medium px-3 py-1.5">Amount</th>
                    <th className="text-right font-medium px-3 py-1.5">TDS</th>
                    <th className="text-right font-medium px-3 py-1.5">Paid</th>
                    <th className="text-right font-medium px-3 py-1.5">Payslip</th>
                  </tr>
                </thead>
                <tbody>
                  {adminStatements.map((r) => (
                    <tr key={r.id || r.month} className="border-t border-zinc-100">
                      <td className="px-3 py-1.5">
                        {r.period_label || r.month}
                        {r.days_worked != null && r.days_worked !== r.days_in_month ? (
                          <span className="text-[11px] text-zinc-500"> ({r.days_worked} of {r.days_in_month} days)</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(r.gross)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">
                        {Number(r.tds || 0) ? money(r.tds) : "\u2014"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{money(r.net)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <button type="button" data-testid={`admin-payslip-${r.month}`}
                                onClick={() => grabPdf(`/staffing/statements/${r.id}/pdf`, `payslip-${r.month}.pdf`)}
                                className="text-xs text-emerald-700 hover:underline">Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {exiting && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
              <div className="text-sm font-medium text-red-800">Exit {person.name}</div>
              <p className="text-xs text-red-700">
                They will no longer be able to sign in, and they will be left out of
                future statement runs, the bank file and payment emails. Everything
                already on file stays.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="text-xs text-zinc-600">Last working day</label>
                  <Input type="date" value={exitDay} className="h-8 w-40"
                         onChange={(e) => setExitDay(e.target.value)}
                         data-testid="staff-exit-day" />
                </div>
                <div className="flex-1 min-w-[12rem]">
                  <label className="text-xs text-zinc-600">Reason (optional)</label>
                  <Input value={exitReason} className="h-8"
                         onChange={(e) => setExitReason(e.target.value)}
                         data-testid="staff-exit-reason" />
                </div>
                <Button size="sm" variant="outline" onClick={() => setExiting(false)}>Cancel</Button>
                <Button size="sm" disabled={exitBusy || !exitDay} onClick={doExit}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="staff-exit-confirm">
                  {exitBusy ? "Working\u2026" : "Confirm exit"}
                </Button>
              </div>
            </div>
          )}

        {staffMissing(person).length > 0 ? (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[13px] text-amber-800">
            Still waiting on them for: {staffMissing(person)
              .map((f) => STAFF_FIELD_LABELS[f] || f).join(", ")}.
          </div>
        ) : (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-800">
            They have filled in everything needed to pay them.
          </div>
        )}

        <div className="space-y-5">
          {STAFF_DETAIL_SECTIONS.map(([title, fields]) => (
            <div key={title}>
              <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mb-1">{title}</p>
              <div className="grid sm:grid-cols-2 gap-x-8">
                {fields.map(([key, label]) => (
                  editing && STAFF_EDITABLE.includes(key) ? (
                    <div key={key} className="py-1">
                      <label className="text-[11px] text-zinc-500">{label}</label>
                      <input className="w-full border border-zinc-300 rounded px-2 py-1 text-[13px]"
                             type={key === "doj" || key === "dob" ? "date"
                                   : key === "monthly_amount" ? "number" : "text"}
                             value={form[key] || ""}
                             onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                             data-testid={`staff-edit-${key}`} />
                    </div>
                  ) : (
                    <StaffRow key={key} label={label}
                              value={key === "monthly_amount" ? inr(person[key])
                                   : key === "tds_enabled" ? (person[key] ? "Deducted at 10%" : "Not deducted")
                                   : person[key]} />
                  )
                ))}
              </div>
            </div>
          ))}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mb-1">Account</p>
          <div className="grid sm:grid-cols-2 gap-x-8">
            <StaffRow label="Login email" value={person.email} />
            <StaffRow label="Last signed in" value={since(person.last_seen_at)} />
          </div>
        </div>

        {editing && (
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <Button variant="outline" onClick={() => setEditing(false)}
                    disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="staff-edit-save">
              {savingEdit ? "Saving..." : "Save changes"}
            </Button>
          </div>
        )}

        <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium pt-2">Documents</p>

        <div className="flex flex-wrap items-end gap-2 border border-zinc-200 rounded-md p-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-zinc-400 block mb-1">Type</label>
            <select className="border border-zinc-200 rounded-md h-9 px-2 text-sm"
                    value={category} onChange={(e) => setCategory(e.target.value)}>
              {STAFF_DOC_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[11px] uppercase tracking-wide text-zinc-400 block mb-1">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. signed copy" />
          </div>
          <input ref={fileRef} type="file" className="hidden"
                 onChange={(e) => upload(e.target.files && e.target.files[0])} />
          <Button disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="staff-doc-upload">
            {busy ? "Uploading..." : "Upload file"}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500 -mt-2">
          PDF, Word, Excel, CSV or images, up to 10 MB. These appear in the contractor's own portal.
        </p>

        {docs.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">No documents yet.</p>
        ) : (
          <>
            <div className="flex justify-end">
              <button className="text-xs text-emerald-700 hover:underline"
                      onClick={() => window.open(`/api/staffing/${pid}/documents/download-all`, "_blank")}>
                Download all as zip
              </button>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>File</TableHead><TableHead>Type</TableHead>
                <TableHead>Size</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      {d.filename}
                      {d.note ? <span className="block text-[11px] text-zinc-400">{d.note}</span> : null}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">{STAFF_DOC_LABELS[d.category] || d.category}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{docSize(d.size)}</TableCell>
                    <TableCell className="text-right">
                      <button className="text-xs text-emerald-700 hover:underline"
                              onClick={() => window.open(`/api/staffing/${pid}/documents/${d.id}/download`, "_blank")}>
                        Download
                      </button>
                      <span className="text-zinc-300 mx-2">|</span>
                      <button className="text-xs text-red-600 hover:underline" onClick={() => remove(d)}>
                        Delete
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
